import React, { useContext, useEffect, useState } from "react";
import "./ChatComponent.css";

import iconCompanie from "../../assets/Artboard 3 copy 6.png";
import DetailChat from "./DetailChat";
import api from "../../utils/api";
import { useChat } from "../../contexts/ChatContext";
import { AuthContext } from "../../providers/AuthProvider";
import CompanyAvatar from "../CompanyAvatar/CompanyAvatar";

const ChatComponent = () => {
  const { user } = useContext(AuthContext);
  const { externalChat } = useChat();
  const [isContactsListOpen, setIsContactsListOpen] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [menssages, setMenssages] = useState([]);
  const [activeChats, setActiveChats] = useState([]);
  const [collapsedChats, setCollapsedChats] = useState({});
  const [lastMessageUpdate, setLastMessageUpdate] = useState(Date.now()); // To force active chats refresh
  const [lastFetchTime, setLastFetchTime] = useState(0); // For throttling
  const [lastActivityTime, setLastActivityTime] = useState(Date.now()); // For adaptive polling
  const [viewedContacts, setViewedContacts] = useState(new Set()); // Track contacts that have been viewed (chat closed)

  const fetchMessages = async () => {
    // Throttling: don't make new request if last one was less than 1 second ago
    const now = Date.now();
    if (isFetching || (now - lastFetchTime < 1000)) {
      return;
    }

    setIsFetching(true);
    setLastFetchTime(now);

    try {
      const { data } = await api.get(`/companies/messages/`);

      const newMessages = data?.data.sort(function (a, b) {
        if (a.attributes.createdAt > b.attributes.createdAt) return -1;
        if (a.attributes.createdAt < b.attributes.createdAt) return 1;
        return 0;
      });

      // Check if there are real changes (not just timestamp differences)
      let hasNewMessages = false;
      if (newMessages.length !== menssages.length) {
        hasNewMessages = true;
      } else if (newMessages.length > 0 && menssages.length > 0) {
        // Compare most recent message ID and unread counts
        const newLatest = newMessages[0];
        const currentLatest = menssages[0];

        if (newLatest.id !== currentLatest.id ||
            newLatest.attributes.unreadCount !== currentLatest.attributes.unreadCount) {
          hasNewMessages = true;
        }
      }

      // Only update state if there are actual changes
      if (hasNewMessages) {
        setMenssages(newMessages);
        setLastMessageUpdate(Date.now());
        setLastActivityTime(Date.now()); // Mark recent activity

        // Remove contacts from viewed list if they have new messages
        if (newMessages.length > 0) {
          const newMessageTalentIds = new Set();
          newMessages.forEach(msg => {
            const otherParty = msg?.attributes?.other_party;
            const sender = msg?.attributes?.sender;
            const receiver = msg?.attributes?.receiver;

            let talent = null;
            if (otherParty?.type === "Talent") {
              talent = otherParty;
            } else if (sender?.type === "Talent") {
              talent = sender;
            } else if (receiver?.type === "Talent") {
              talent = receiver;
            }

            if (talent?.id) {
              newMessageTalentIds.add(String(talent.id));
            }
          });

          // Remove any talents with new messages from viewed contacts
          setViewedContacts(prev => {
            const newViewed = new Set(prev);
            newMessageTalentIds.forEach(talentId => {
              newViewed.delete(talentId);
            });
            return newViewed;
          });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsFetching(false);
    }
  };

  const handleOpenChat = (receiverId) => {
    receiverId = String(receiverId);
    setActiveChats(prev => prev.includes(receiverId) ? prev : [...prev, receiverId]);
    setCollapsedChats(prev => ({
      ...prev,
      [receiverId]: false
    }));
  };

  const handleCloseChat = (receiverId) => {
    receiverId = String(receiverId);
    setActiveChats(prev => prev.filter(id => id !== receiverId));

    // Mark contact as viewed when chat is closed
    setViewedContacts(prev => new Set([...prev, receiverId]));
  };

  const handleCollapseChat = (receiverId, value) => {
    receiverId = String(receiverId);
    setCollapsedChats(prev => ({
      ...prev,
      [receiverId]: value
    }));
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  useEffect(() => {
    let intervalId = null;

    // Adaptive polling: faster if there was recent activity
    const getPollingInterval = () => {
      const timeSinceActivity = Date.now() - lastActivityTime;
      if (timeSinceActivity < 30000) { // Less than 30 seconds since last activity
        return 1500; // 1.5 seconds - fast polling
      } else if (timeSinceActivity < 120000) { // Less than 2 minutes
        return 3000; // 3 seconds - medium polling
      } else {
        return 5000; // 5 seconds - slow polling
      }
    };

    const startPolling = () => {
      intervalId = setInterval(() => {
        fetchMessages();
        // Reschedule with new interval
        clearInterval(intervalId);
        startPolling();
      }, getPollingInterval());
    };

    startPolling();

    return () => clearInterval(intervalId);
  }, [isFetching, lastActivityTime]);

  // Effect to open external chat
  useEffect(() => {
    if (externalChat?.id) {
      handleOpenChat(externalChat.id);
    }
  }, [externalChat?.id]);

  const toggleContactsList = () => {
    setIsContactsListOpen(!isContactsListOpen);
  };

  // Filter to show only the most recent message from each talent
  const uniqueContacts = [];
  const seenTalents = new Set();

  for (const msg of menssages) {
    const sender = msg?.attributes?.sender;
    const receiver = msg?.attributes?.receiver;
    const otherParty = msg?.attributes?.other_party;

    // Prioritize other_party if available, otherwise identify talent from sender/receiver
    let talent = null;
    if (otherParty?.type === "Talent") {
      talent = otherParty;
    } else if (sender?.type === "Talent") {
      talent = sender;
    } else if (receiver?.type === "Talent") {
      talent = receiver;
    }

    // If we found a talent and haven't seen them before, add to unique contacts
    if (talent?.id && !seenTalents.has(talent.id)) {
      // Create a normalized message object with talent as receiver for consistency
      const normalizedMsg = {
        ...msg,
        attributes: {
          ...msg.attributes,
          receiver: talent
        }
      };
      uniqueContacts.push(normalizedMsg);
      seenTalents.add(talent.id);
    }
  }

  // Calculate total unread messages
  const totalUnreadCount = uniqueContacts.reduce((total, contact) => {
    return total + (contact?.attributes?.unreadCount || 0);
  }, 0);

  return (
    <div className="chat-system-container">
      {/* Main Contacts List (collapsed or expanded) */}
      {isContactsListOpen ? (
        <div className="contacts-list-container">
          <div className="contacts-header" onClick={toggleContactsList} style={{cursor: 'pointer'}}>
            <div className="containerTitleHeader">
              <CompanyAvatar
                avatar={user?.avatar}
                width="20px"
                height="20px"
              />
              <span>Mensagens</span>
            </div>
            <button className="dropdown-button" onClick={toggleContactsList} style={{background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginRight: 0, height: '100%', display: 'flex', alignItems: 'center'}}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 9L12 15L18 9"
                  stroke="#000"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
          <div className="contacts-scroll">
            {uniqueContacts.map((contact) => (
              <DetailChat
                key={contact?.attributes?.receiver?.id}
                id={String(contact?.attributes?.receiver?.id)}
                messageId={contact.id}
                nameTalents={contact?.attributes?.receiver?.name}
                data={contact}
                isActive={false}
                onOpenChat={handleOpenChat}
                onCloseChat={handleCloseChat}
                activeChats={activeChats}
                isCollapsed={collapsedChats[String(contact?.attributes?.receiver?.id)]}
                onCollapseChat={handleCollapseChat}
                isContactsListOpen={isContactsListOpen}
                inList={true}
                activeChatIds={activeChats}
                unreadCount={contact?.attributes?.unreadCount || 0}
                lastMessageUpdate={lastMessageUpdate}
                viewedContacts={viewedContacts}
              />
            ))}
          </div>
          <div style={{ height: "10px", width: "100%" }}></div>
        </div>
      ) : (
        <div className="contacts-collapsed">
          <div className="contacts-collapsed-header" onClick={toggleContactsList} style={{cursor: 'pointer'}}>
            <div className="containerTitleHeader">
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CompanyAvatar
                  avatar={user?.avatar}
                  width="20px"
                  height="20px"
                />
                {totalUnreadCount > 0 && (
                  <div className="unread-badge">
                    {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                  </div>
                )}
              </div>
              <span>Mensagens</span>
            </div>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 15L12 9L18 15"
                stroke="#000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      )}

      {/* Render active chat windows */}
      {activeChats.map(receiverId => {
        let chatData = menssages.find(msg => {
          const sender = msg?.attributes?.sender;
          const receiver = msg?.attributes?.receiver;
          const otherParty = msg?.attributes?.other_party;

          // Check if other_party matches, otherwise check sender/receiver
          let talentId = null;
          if (otherParty?.type === "Talent") {
            talentId = otherParty.id;
          } else if (sender?.type === "Talent") {
            talentId = sender.id;
          } else if (receiver?.type === "Talent") {
            talentId = receiver.id;
          }

          return String(talentId) === receiverId;
        });

        // If not found, but is the external chat, create a minimal object
        if (!chatData && externalChat && String(externalChat.id) === receiverId) {
          chatData = {
            id: null,
            attributes: {
              receiver: externalChat,
              textMsg: '',
              createdAt: new Date().toISOString(),
              readed: true,
              unreadCount: 0,
            }
          };
        } else if (chatData) {
          // Normalize the chat data to have talent as receiver
          const sender = chatData?.attributes?.sender;
          const receiver = chatData?.attributes?.receiver;
          const otherParty = chatData?.attributes?.other_party;

          let talent = null;
          if (otherParty?.type === "Talent") {
            talent = otherParty;
          } else if (sender?.type === "Talent") {
            talent = sender;
          } else if (receiver?.type === "Talent") {
            talent = receiver;
          }

          if (talent) {
            chatData = {
              ...chatData,
              attributes: {
                ...chatData.attributes,
                receiver: talent
              }
            };
          }
        }

        // If still no data, do not render
        if (!chatData) return null;

        return (
          <DetailChat
            key={receiverId}
            id={receiverId}
            messageId={chatData.id}
            nameTalents={chatData?.attributes?.receiver?.name}
            data={chatData}
            isActive={true}
            onOpenChat={handleOpenChat}
            onCloseChat={handleCloseChat}
            activeChats={activeChats}
            isCollapsed={collapsedChats[receiverId]}
            onCollapseChat={handleCollapseChat}
            isContactsListOpen={isContactsListOpen}
            inList={false}
            activeChatIds={activeChats}
            unreadCount={chatData?.attributes?.unreadCount || 0}
            lastMessageUpdate={lastMessageUpdate}
            viewedContacts={viewedContacts}
          />
        );
      })}
    </div>
  );
};

export default ChatComponent;
